import React, { useState, useEffect, useRef } from 'react';

function CalendarView({ onDayClick, onConvoClick }) {
  var [heatmap, setHeatmap] = useState([]);
  var [loading, setLoading] = useState(true);
  var [selectedDay, setSelectedDay] = useState(null);
  var [dayDetail, setDayDetail] = useState(null);
  var [dayLoading, setDayLoading] = useState(false);
  var [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  var [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  var [calTheme, setCalTheme] = useState(localStorage.getItem('cal_theme') || 'lavender');
  var touchStartX = useRef(null);

  useEffect(function() {
    fetch('/api/calendar/heatmap')
      .then(function(r) { return r.json(); })
      .then(function(data) {
        setHeatmap(data || []);
        setLoading(false);
      })
      .catch(function() { setLoading(false); });
    // 监听localStorage变化（从Settings切换时）
    var onStorage = function() {
      var t = localStorage.getItem('cal_theme');
      if (t) setCalTheme(t);
    };
    window.addEventListener('storage', onStorage);
    // 也轮询一下（同tab内localStorage不触发storage事件）
    var poll = setInterval(function() {
      var t = localStorage.getItem('cal_theme');
      if (t && t !== calTheme) setCalTheme(t);
    }, 500);
    return function() { window.removeEventListener('storage', onStorage); clearInterval(poll); };
  }, []);

  var getLevel = function(count, maxCount) {
    if (count === 0) return 0;
    if (!maxCount || maxCount === 0) return 1;
    var ratio = count / maxCount;
    if (ratio <= 0.08) return 1;
    if (ratio <= 0.18) return 2;
    if (ratio <= 0.32) return 3;
    if (ratio <= 0.52) return 4;
    if (ratio <= 0.78) return 5;
    return 6;
  };

  var formatDate = function(dateStr) {
    var parts = dateStr.split('-');
    return parseInt(parts[1]) + '月' + parseInt(parts[2]) + '日';
  };

  var formatWeekday = function(dateStr) {
    var d = new Date(dateStr + 'T12:00:00');
    var days = ['日', '一', '二', '三', '四', '五', '六'];
    return '周' + days[d.getDay()];
  };

  var handleDayClick = async function(day) {
    if (!day || day.count === 0) return;
    setSelectedDay(day.date);
    setDayLoading(true);
    setDayDetail(null);
    try {
      var resp = await fetch('/api/calendar/day/' + day.date);
      var data = await resp.json();
      setDayDetail(data);
    } catch(e) { console.error('Day detail error:', e); }
    setDayLoading(false);
  };

  var closeDetail = function() {
    setSelectedDay(null);
    setDayDetail(null);
  };

  var prevMonth = function() {
    if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear(currentYear - 1); }
    else { setCurrentMonth(currentMonth - 1); }
  };

  var nextMonth = function() {
    if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear(currentYear + 1); }
    else { setCurrentMonth(currentMonth + 1); }
  };

  var handleTouchStart = function(e) {
    touchStartX.current = e.touches[0].clientX;
  };

  var handleTouchEnd = function(e) {
    if (touchStartX.current === null) return;
    var diff = e.changedTouches[0].clientX - touchStartX.current;
    if (diff > 60) prevMonth();
    else if (diff < -60) nextMonth();
    touchStartX.current = null;
  };

  // 构建当月日历网格
  var buildMonthGrid = function() {
    var firstDay = new Date(currentYear, currentMonth, 1);
    var startDow = firstDay.getDay(); // 0=周日
    var daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();

    // 构建 heatmap 查找 map
    var dayMap = {};
    heatmap.forEach(function(d) { dayMap[d.date] = d; });

    var cells = [];
    // 前面补空
    for (var pad = 0; pad < startDow; pad++) {
      cells.push(null);
    }
    // 填充每天
    for (var d = 1; d <= daysInMonth; d++) {
      var dateStr = currentYear + '-' + String(currentMonth + 1).padStart(2, '0') + '-' + String(d).padStart(2, '0');
      var data = dayMap[dateStr] || { date: dateStr, count: 0, starred: false };
      cells.push(data);
    }
    // 后面补空到满行
    while (cells.length % 7 !== 0) { cells.push(null); }

    var rows = [];
    for (var i = 0; i < cells.length; i += 7) {
      rows.push(cells.slice(i, i + 7));
    }
    return rows;
  };

  var monthNames = ['1月','2月','3月','4月','5月','6月','7月','8月','9月','10月','11月','12月'];

  // 当月统计
  var monthPrefix = currentYear + '-' + String(currentMonth + 1).padStart(2, '0');
  var monthData = heatmap.filter(function(d) { return d.date.startsWith(monthPrefix); });
  var monthMsgs = monthData.reduce(function(s, d) { return s + d.count; }, 0);
  var monthDays = monthData.filter(function(d) { return d.count > 0; }).length;
  var monthMax = monthData.reduce(function(max, d) { return d.count > max ? d.count : max; }, 0);

  var rows = buildMonthGrid();
  var today = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Hong_Kong' });

  if (loading) {
    return React.createElement('div', { className: 'cal-loading' }, '加载中...');
  }

  return React.createElement('div', { className: 'calendar-view' },
    // 月份导航
    React.createElement('div', { className: 'cal-month-nav' },
      React.createElement('button', { className: 'cal-nav-btn', onClick: prevMonth }, '‹'),
      React.createElement('div', { className: 'cal-month-title' }, currentYear + '年 ' + monthNames[currentMonth]),
      React.createElement('button', { className: 'cal-nav-btn', onClick: nextMonth }, '›')
    ),
    // 统计栏
    React.createElement('div', { className: 'cal-stats' },
      React.createElement('div', { className: 'cal-stat' },
        React.createElement('div', { className: 'cal-stat-num' }, monthMsgs.toLocaleString()),
        React.createElement('div', { className: 'cal-stat-label' }, '条消息')
      ),
      React.createElement('div', { className: 'cal-stat' },
        React.createElement('div', { className: 'cal-stat-num' }, monthDays),
        React.createElement('div', { className: 'cal-stat-label' }, '天有对话')
      ),
      React.createElement('div', { className: 'cal-stat' },
        React.createElement('div', { className: 'cal-stat-num' }, monthMax),
        React.createElement('div', { className: 'cal-stat-label' }, '最多一天')
      )
    ),
    // 月历网格
    React.createElement('div', {
      className: 'cal-month-grid-wrap',
      onTouchStart: handleTouchStart,
      onTouchEnd: handleTouchEnd
    },
      // 星期标题
      React.createElement('div', { className: 'cal-week-header' },
        ['日','一','二','三','四','五','六'].map(function(d) {
          return React.createElement('div', { key: d, className: 'cal-week-label' }, d);
        })
      ),
      // 日历格子
      rows.map(function(row, ri) {
        return React.createElement('div', { key: ri, className: 'cal-month-row' },
          row.map(function(day, ci) {
            if (!day) return React.createElement('div', { key: ci, className: 'cal-month-cell empty' });
            var level = getLevel(day.count, monthMax);
            var isToday = day.date === today;
            var isSelected = selectedDay === day.date;
            var dayNum = parseInt(day.date.split('-')[2]);
            return React.createElement('div', {
              key: ci,
              className: 'cal-month-cell level-' + level + (isToday ? ' today' : '') + (isSelected ? ' selected' : '') + (day.count > 0 ? ' has-data' : ''),
              onClick: function() { handleDayClick(day); }
            },
              React.createElement('div', { className: 'cal-day-num' }, dayNum),
              day.count > 0 && React.createElement('div', { className: 'cal-day-count' }, day.count),
              day.starred && React.createElement('div', { className: 'cal-day-star' }, '★')
            );
          })
        );
      }),
      // 图例
      React.createElement('div', { className: 'cal-legend' },
        React.createElement('span', { className: 'cal-legend-text' }, '少'),
        [0,1,2,3,4,5,6].map(function(l) {
          return React.createElement('div', { key: l, className: 'cal-cell legend level-' + l });
        }),
        React.createElement('span', { className: 'cal-legend-text' }, '多')
      )
    ),
    // 日详情弹窗
    selectedDay && React.createElement('div', { className: 'cal-detail-overlay', onClick: closeDetail },
      React.createElement('div', { className: 'cal-detail-card', onClick: function(e) { e.stopPropagation(); } },
        React.createElement('div', { className: 'cal-detail-header' },
          React.createElement('div', { className: 'cal-detail-date' }, formatDate(selectedDay) + ' ' + formatWeekday(selectedDay)),
          React.createElement('button', { className: 'cal-detail-close', onClick: closeDetail }, '×')
        ),
        dayLoading
          ? React.createElement('div', { className: 'cal-detail-loading' }, '加载中...')
          : dayDetail && React.createElement('div', { className: 'cal-detail-body' },
              React.createElement('div', { className: 'cal-detail-count' },
                dayDetail.message_count + ' 条消息' + (dayDetail.conversation_title ? '  ·  ' + dayDetail.conversation_title : '')
              ),
              dayDetail.summaries && dayDetail.summaries.length > 0 && React.createElement('div', { className: 'cal-detail-section' },
                React.createElement('div', { className: 'cal-detail-section-title' }, '日摘要'),
                dayDetail.summaries.map(function(s, i) {
                  return React.createElement('div', { key: i, className: 'cal-detail-summary' }, s.summary);
                })
              ),
              dayDetail.messages && dayDetail.messages.length > 0 && React.createElement('div', { className: 'cal-detail-section' },
                React.createElement('div', { className: 'cal-detail-section-title' }, '消息预览'),
                React.createElement('div', { className: 'cal-detail-msgs' },
                  dayDetail.messages.slice(-20).map(function(m, i) {
                    var time = new Date(m.created_at).toLocaleTimeString('zh-CN', { timeZone: 'Asia/Hong_Kong', hour: '2-digit', minute: '2-digit' });
                    var isAiden = m.sender === 'aiden';
                    var text = (m.text || '').substring(0, 80);
                    if (text === '[sticker]') text = '[表情包]';
                    return React.createElement('div', { key: i, className: 'cal-msg-item' + (isAiden ? ' aiden' : ' zhizhi') },
                      React.createElement('span', { className: 'cal-msg-time' }, time),
                      React.createElement('span', { className: 'cal-msg-sender' }, isAiden ? '晏' : '枝'),
                      React.createElement('span', { className: 'cal-msg-text' }, text)
                    );
                  })
                )
              ),
              dayDetail.conversation_id && React.createElement('button', {
                className: 'cal-detail-jump',
                onClick: function() { if (onConvoClick) onConvoClick(dayDetail.conversation_id); closeDetail(); }
              }, '查看完整对话 →')
            )
      )
    )
  );
}

export default CalendarView;
